const getValueOrNA = (value) => {
	if (typeof value !== "string") return "N/A";
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : "N/A";
};

const getWebsiteHref = (website) => {
	if (typeof website !== "string") return null;
	const trimmed = website.trim();
	if (!trimmed) return null;

	if (/^https?:\/\//i.test(trimmed)) {
		return trimmed;
	}

	return `https://${trimmed}`;
};

const CompanyProfileSummaryCard = ({
	company,
	isActivelyHiring = false,
	showTabsShell = false,
	action = null,
	className = "",
	cardPadding = true,
	showJobsPreview = false,
}) => {
	const companyName = getValueOrNA(company?.company_name);
	const specialties = getValueOrNA(company?.specialties);
	const industry = getValueOrNA(company?.company_industry);
	const headquarters = getValueOrNA(company?.headquarters);
	const website = getValueOrNA(company?.company_website);
	const websiteHref = getWebsiteHref(company?.company_website);

	return (
		<div
			className={`w-full min-w-0 overflow-hidden rounded-xl bg-white ${className}`}
		>
			<div className={`${cardPadding ? "p-4 sm:p-6" : "p-0"}`}>
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
						<div className="flex min-w-0 items-center gap-4">
							<div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border border-gray-100 bg-white sm:h-20 sm:w-20">
								<img
									alt={`${companyName} logo`}
									loading="lazy"
									className="h-full w-full object-cover"
									src={company?.logo_url || "/images/co-placeholder.png"}
									onError={(e) => (e.target.src = "/images/co-placeholder.png")}
								/>
							</div>
							<div className="min-w-0">
								<h2 className="break-words text-xl font-bold text-gray-900 font-plus-jakarta sm:text-2xl">
									{companyName}
								</h2>
								<p className="mt-1 break-words text-sm text-gray-700 font-inter sm:text-base">
									{specialties}
								</p>
							</div>
						</div>

						{action && <div className="md:ml-4 md:flex-shrink-0">{action}</div>}
					</div>

					<div className="flex flex-wrap gap-2 text-xs text-gray-700 font-inter">
						<span className="inline-flex items-center gap-1 rounded-full bg-gray-100/80 px-2.5 py-0.5 font-medium text-gray-800">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth="1.5"
								stroke="currentColor"
								className="h-3 w-3 text-gray-600"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
								/>
							</svg>
							<span className="break-words">{industry}</span>
						</span>
						<span className="inline-flex items-center gap-1 rounded-full bg-gray-100/80 px-2.5 py-0.5 font-medium text-gray-800">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth="1.5"
								stroke="currentColor"
								className="h-3 w-3 text-gray-600"
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
								/>
							</svg>
							<span className="break-words">{headquarters}</span>
						</span>
						{isActivelyHiring && (
							<span className="inline-flex items-center gap-1 rounded-full bg-success-light px-2.5 py-0.5 font-medium text-success-default">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth="1.5"
									stroke="currentColor"
									className="h-3 w-3 text-green-600"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0M12 12.75h.008v.008H12v-.008Z"
									/>
								</svg>
								Actively Hiring
							</span>
						)}
					</div>

					<div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-gray-600 font-inter">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="1.5"
							stroke="currentColor"
							className="w-5 h-5 text-gray-600"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="m6.115 5.19.319 1.913A6 6 0 0 0 8.11 10.36L9.75 12l-.387.775c-.217.433-.132.956.21 1.298l1.348 1.348c.21.21.329.497.329.795v1.089c0 .426.24.815.622 1.006l.153.076c.433.217.956.132 1.298-.21l.723-.723a8.7 8.7 0 0 0 2.288-4.042 1.087 1.087 0 0 0-.358-1.099l-1.33-1.108c-.251-.21-.582-.299-.905-.245l-1.17.195a1.125 1.125 0 0 1-.98-.314l-.295-.295a1.125 1.125 0 0 1 0-1.591l.13-.132a1.125 1.125 0 0 1 1.3-.21l.603.302a.809.809 0 0 0 1.086-1.086L14.25 7.5l1.256-.837a4.5 4.5 0 0 0 1.528-1.732l.146-.292M6.115 5.19A9 9 0 1 0 17.18 4.64M6.115 5.19A8.965 8.965 0 0 1 12 3c1.929 0 3.716.607 5.18 1.64"
							/>
						</svg>
						<span className="font-semibold">Website:</span>
						{websiteHref ? (
							<a
								href={websiteHref}
								target="_blank"
								rel="noopener noreferrer"
								className="min-w-0 break-all font-medium text-primary-700 hover:underline"
							>
								{website}
							</a>
						) : (
							<span className="break-words">{website}</span>
						)}
					</div>
				</div>
			</div>

			{showTabsShell && (
				<div className="border-t border-gray-200 px-4 sm:px-6">
					<nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
						<span className="cursor-default whitespace-nowrap border-b-2 border-gray-900 py-3 px-1 text-sm font-semibold text-gray-900">
							Jobs
						</span>
						<span className="cursor-default whitespace-nowrap border-b-2 border-transparent py-3 px-1 text-sm font-semibold text-gray-500">
							About
						</span>
					</nav>
				</div>
			)}

			{showJobsPreview && (
				<div className="border-t border-gray-200 bg-gray-50/60 p-3 sm:p-4">
					<h3 className="text-base sm:text-lg font-semibold text-gray-900 font-plus-jakarta">Open Roles</h3>
					<div className="mt-3 space-y-2.5">
						{["Frontend Developer", "Backend Developer"].map((role) => (
							<div
								key={role}
								className={`items-center justify-between rounded-lg border border-gray-100 bg-white px-3 py-2 sm:px-4 sm:py-3 ${
									role === "Backend Developer" ? "hidden lg:flex" : "flex"
								}`}
							>
								<div className="min-w-0">
									<p className="truncate text-sm font-semibold text-gray-900">{role}</p>
									<p className="truncate text-xs text-gray-500">
										{headquarters} • Negotiable
									</p>
								</div>
								<button
									type="button"
									className="ml-3 rounded-md bg-primary-700 px-3 py-1.5 text-xs font-semibold text-white"
								>
									Explore
								</button>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default CompanyProfileSummaryCard;
